import { AppstoreOutlined, SettingOutlined } from '@ant-design/icons';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MenuClickSource } from '~/domain/enum/menuClickSource.enum';
import { formatToday, getFirstName, getGreeting } from '~/helpers/util/greeting';
import { useQuickAccess } from '~/hooks/useQuickAccess';
import type { QuickAccessItem } from '~/hooks/useQuickAccess';
import { selectors } from '~/store/modules';
import { addTab } from '~/store/modules/tabs/actions';

import QuickAccessSettingsModal from './QuickAccessSettingsModal';
import * as S from './QuickAccess.styled';

export default function QuickAccess() {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const profile = useSelector(selectors.ui.auth.getProfile);

  const {
    shortcuts,
    frequents,
    recents,
    options,
    pinnedKeys,
    savePins,
    registerAccess,
    hasPermissions,
  } = useQuickAccess();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const openShortcut = (item: QuickAccessItem) => {
    registerAccess(item.key);
    posthog.capture('menu_click', {
      menu: item.key,
      menuName: item.menu.name,
      permissionName: item.menu.permissionName,
      userName: profile?.veiling_claim_type_name,
      source: MenuClickSource.QuickAccess,
    });
    dispatch(addTab(item.menu));
  };

  const handleSave = (keys: string[]) => {
    savePins(keys);
    setIsSettingsOpen(false);
  };

  const renderCard = (item: QuickAccessItem) => (
    <S.Card key={item.key} type="button" onClick={() => openShortcut(item)}>
      <S.IconBadge>{item.icon ?? <AppstoreOutlined />}</S.IconBadge>
      <S.CardTexts>
        <S.CardTitle>{item.title}</S.CardTitle>
        {!!item.parentTitle && <S.CardParent>{item.parentTitle}</S.CardParent>}
      </S.CardTexts>
    </S.Card>
  );

  if (!hasPermissions) return null;

  return (
    <S.Container>
      <S.Hero>
        <S.HeroDate>{formatToday()}</S.HeroDate>
        <S.HeroGreeting>
          {getGreeting()}, {getFirstName(profile?.veiling_claim_type_name)}
        </S.HeroGreeting>
      </S.Hero>

      <S.Header>
        <S.HeaderTexts>
          <S.Title>Acesso rápido</S.Title>
          <S.Subtitle>Personalize com os menus que você mais usa</S.Subtitle>
        </S.HeaderTexts>
        <S.CustomizeButton type="button" onClick={() => setIsSettingsOpen(true)}>
          <SettingOutlined />
          Personalizar
        </S.CustomizeButton>
      </S.Header>

      <S.Grid>{shortcuts.map(renderCard)}</S.Grid>

      {!!frequents.length && (
        <S.Section>
          <S.Header>
            <S.HeaderTexts>
              <S.Title>Menus frequentes</S.Title>
              <S.Subtitle>Os mais acessados no portal</S.Subtitle>
            </S.HeaderTexts>
          </S.Header>

          <S.Grid>{frequents.map(renderCard)}</S.Grid>
        </S.Section>
      )}

      {!!recents.length && (
        <S.RecentsWrapper>
          <S.RecentsTitle>Últimos acessados</S.RecentsTitle>
          <S.Chips>
            {recents.map(item => (
              <S.Chip key={item.key} type="button" onClick={() => openShortcut(item)}>
                {item.title}
              </S.Chip>
            ))}
          </S.Chips>
        </S.RecentsWrapper>
      )}

      <QuickAccessSettingsModal
        isOpen={isSettingsOpen}
        options={options}
        pinnedKeys={pinnedKeys}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSave}
      />
    </S.Container>
  );
}
